import { Collapse, Divider, message, Typography } from "antd";
import { type InjectedFormikProps, withFormik, type WithFormikConfig } from "formik";
import { groupBy } from "lodash-es";
import moment, { type Moment } from "moment";
import { type RangeValue } from "rc-picker/lib/interface";
import { useCallback, useEffect, useMemo, useState } from "react";

import { BTDropdown } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";
import type { BuilderFilterTypes } from "@btnet/services-btnet/handlers/models/builderFilterTypes";
import { cn } from "@btnet/utilities-core";
import { isWebview } from "@btnet/utilities-webview";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import {
    ClosedAccountTypes,
    DateRangeFilterOptionItem,
    FilterType,
    NumberRangeOptionItem,
} from "types/enum";

import { MANAGE_SAVED_FILTERS } from "utilities/historyState/actionWithHistory.utility";
import { isNullOrUndefined } from "utilities/object/object";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCheckbox } from "commonComponents/btWrappers/BTCheckbox/BTCheckbox";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTCollapse } from "commonComponents/btWrappers/BTCollapse/BTCollapse";
import { btDeleteConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTDatePicker } from "commonComponents/btWrappers/BTDatePicker/BTDatePicker";
import { BTDrawer } from "commonComponents/btWrappers/BTDrawer/BTDrawer";
import { BTForm, BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTFormattedPlural } from "commonComponents/btWrappers/BTFormattedPlural/BTFormattedPlural";
import {
    BTIconCaretRightOutlined,
    BTIconCloseOutlined,
    BTIconEllipsisOutlined,
    BTIconSettingFilled,
} from "commonComponents/btWrappers/BTIcon";
import { BTInput } from "commonComponents/btWrappers/BTInput/BTInput";
import { BTInputNumber } from "commonComponents/btWrappers/BTInputNumber/BTInputNumber";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { BTSelectDuplicate } from "commonComponents/btWrappers/BTSelect/BTSelectDuplicate";
import { BTTimePicker } from "commonComponents/btWrappers/BTTimePicker/BTTimePicker";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { BuilderSearch } from "commonComponents/entity/builder/BuilderSearch/BuilderSearch";
import {
    DateRange,
    type IDateRangeEntity,
    type IDateRangeFormValues,
} from "commonComponents/entity/filters/DateRange/DateRange";
import { FilterSelectCustomItem } from "commonComponents/entity/filters/FilterSelectCustomItem/FilterSelectCustomItem";
import { Currency } from "commonComponents/financial/Currency/Currency";
import {
    type INumberRangeEntity,
    type INumberRangeFormValues,
    NumberRange,
} from "commonComponents/utilities/NumberRange/NumberRange";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";

import { FilterCount } from "entity/filters/Filter/FilterCount";
import { getAppliedFilterCount, getFilterString } from "entity/filters/filters.utils";
import SavedFilter from "entity/filters/SavedFilter/SavedFilter";
import type { ISavedFilterHandler } from "entity/filters/SavedFilter/SavedFilter.api.handler";
import {
    type ISavedFilterFormValues,
    SavedFilterEntity,
    SavedFilterItem,
} from "entity/filters/SavedFilter/SavedFilter.api.types";
import { type ISavedViewsAndFiltersCardFormValues } from "entity/filters/SavedViewsAndFiltersList/SavedViewsAndFiltersCard/SavedViewsAndFiltersCard.api.types";
import { SavedViewsAndFiltersSettings } from "entity/filters/SavedViewsAndFiltersList/SavedViewsAndFiltersSettings/SavedViewsAndFiltersSettings";
import { useSavedViewsSettingsState } from "entity/filters/SavedViewsAndFiltersList/useSavedViewsSettingsState";
import { useBuilderDateFilterUtils } from "entity/filters/useBuilderDateFilterUtils";

import "./Filter.less";

import {
    DateRangeValue,
    type FilterEntity,
    type FilterEntityType,
    type FilterFormActions,
    type FilterItem,
    getMultiSelectValue,
    getSelectedFilterItemsFromSavedFilter,
    type IFilterFormValues,
    isStandardFilter,
    type KeywordValue,
    mapFilterEntityToFormValues,
    NumberRangeValue,
    SelectedFilterItem,
    type SelectedLazyFormValue,
    type SelectedValueTypes,
    StandardFilterId,
} from "./Filter.api.types";

const { Panel } = Collapse;
const { Text } = Typography;

interface IFilterProps {
    actionBeingPerformed: FilterFormActions;
    appliedFilter?: SelectedFilterItem[];
    defaultOpen: boolean;
    drawerOpen?: boolean;
    entity: FilterEntity;
    filterCount?: number;
    filterTypeEntity: FilterEntityType;
    isInPopup: boolean;
    jobIDs?: number[];
    jobPickerFilterOverride: SelectedFilterItem[] | undefined;
    saveFilterHandler?: ISavedFilterHandler;
    selectedFilterId?: number;
    showHasUnsavedChanges: boolean;
    showSaveButton: boolean;
    showSavedFilters: boolean;
    useDrawer?: boolean;
    onSavedFilterAdded: (
        id: number,
        savedFilterValues: ISavedFilterFormValues,
        values: IFilterFormValues,
    ) => Promise<void>;
    onSavedFilterDeleted: (
        id: number,
        newValues: IFilterFormValues,
        wasDefaultDeleted?: boolean,
    ) => Promise<void>;
    onSavedFilterUpdated: (
        id: number,
        savedFilterValues: ISavedFilterFormValues,
        values: IFilterFormValues,
        canEdit: boolean,
    ) => Promise<void>;
    onSavedFilterUpdatedClick: (id: number, newValues: ISavedFilterFormValues) => Promise<void>;
    onSavedFilterVisibleChange?: (visible: boolean) => void;
    onSetDefaultFilter: (newDefault: SavedFilterItem) => Promise<void>;
    onSubmit: (values: IFilterFormValues) => Promise<void>;
    onClear?: () => void;
    setDrawerOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    setFilterCount?: (count: number) => void;
    setFilterPreferences: (isPanelOpen: boolean) => void;
    setSelectedFilterId: (id: number) => void;
    /** When true, expands the filter drawer to 95vw in mobile webview to meet mobile usability criteria */
    forceWebviewWidth?: boolean;
}

interface IFilterItemHelperProps extends InjectedFormikProps<IFilterProps, IFilterFormValues> {
    entityFilter: FilterItem;
    filter: SelectedFilterItem | undefined;
    isDisabled?: boolean;
    isInPopup: boolean;
    options: Record<string, BTSelectItem<undefined>[]>;
    handleChangeBuilderSearch: (
        key: string,
        selectedItem: BTSelectItem<unknown> | undefined,
    ) => void;
    handleChangeCustom: (key: string, value: SelectedValueTypes) => void;
    handleDateChange: (key: string, formValue: IDateRangeFormValues) => void;
    handleKeywordSearch: (key: string, selectValue: number, textValue: string) => void;
    handleNumberRange: (key: string, formValue: INumberRangeFormValues) => void;
    isSemiMonthDateDisabled: (currentDate: Moment | null) => boolean;
    isWeekDateDisabled: (currentDate: Moment | null) => boolean;
    onDateRangeChange: (
        field: string,
        value: DateRangeFilterOptionItem,
        selectedItem?: BTSelectItem<undefined>,
    ) => void;
}

/**
 * Returns individual filter components based on filter type
 * @param filter: filter to render
 * @param index: index for option lookup
 */
const FilterItemHelper = (props: IFilterItemHelperProps) => {
    const {
        entity,
        entityFilter,
        filter,
        isDisabled,
        isInPopup,
        useDrawer,
        handleBlur,
        handleChangeBuilderSearch,
        handleChangeCustom,
        handleDateChange,
        handleKeywordSearch,
        handleNumberRange,
        onDateRangeChange,
        setFieldTouched,
    } = props;
    const { options } = entity;
    const selectDropdownsCanExpand = useDrawer === undefined || useDrawer === false;

    if (!entityFilter || !entityFilter.isVisible || !filter) {
        return null;
    }

    // If the filter is disabled, reset the selected value to the default value
    const selectedValue = isDisabled ? entityFilter.defaultValue : filter.selectedValue;

    const label = entityFilter.showLabel && (
        <div>
            {entityFilter.title}
            {entityFilter.tooltipText && (
                <BTPopover
                    content={
                        // eslint-disable-next-line react/no-danger
                        <div dangerouslySetInnerHTML={{ __html: entityFilter.tooltipText }} />
                    }
                />
            )}
            {isDisabled && <div className="labelDescriptor">{entityFilter.disabledDescriptor}</div>}
        </div>
    );

    switch (filter.type) {
        case FilterType.Checkbox: {
            return (
                <BTCol className="FilterPresentational-FilterElement">
                    <BTFormItem label={label}>
                        <BTCheckbox
                            id={filter.key}
                            data-testid={filter.key}
                            checked={selectedValue as boolean}
                            disabled={isDisabled}
                            onChange={(e) => handleChangeCustom(filter.key, e.target.checked)}
                        />
                    </BTFormItem>
                </BTCol>
            );
        }
        case FilterType.SingleSelect: {
            return (
                <BTCol
                    className="FilterPresentational-FilterElement"
                    span={isInPopup || useDrawer ? 24 : 3}
                >
                    <BTFormItem label={label}>
                        <BTSelect
                            id={filter.key}
                            data-testid={filter.key}
                            disabled={isDisabled}
                            onChange={(field, value) => handleChangeCustom(field, value.toString())}
                            onBlur={setFieldTouched}
                            value={Number(selectedValue)}
                            treeData={options[entityFilter.options]}
                            noTreeDataPlaceholder={entityFilter.placeholderText}
                            dropdownCanExpand={selectDropdownsCanExpand}
                        />
                    </BTFormItem>
                </BTCol>
            );
        }
        case FilterType.Date: {
            return (
                <BTCol className="FilterPresentational-FilterElement">
                    <BTFormItem label={label}>
                        <BTDatePicker
                            id={filter.key}
                            data-testid={filter.key}
                            value={moment(selectedValue as string)}
                            disabled={isDisabled}
                            onChange={(field, value) =>
                                handleChangeCustom(field.toString(), value?.format("YYYY-MM-DD"))
                            }
                        />
                    </BTFormItem>
                </BTCol>
            );
        }
        case FilterType.MultiSelect:
        case FilterType.GroupedMultiSelect: {
            const multiselectValue = getMultiSelectValue(selectedValue);
            const flattenedFilterOptions =
                filter.type === FilterType.GroupedMultiSelect
                    ? options[entityFilter.options].flatMap((x) => x.children)
                    : options[entityFilter.options];
            const groupedOptionsById = groupBy(flattenedFilterOptions, "id");
            const hasDuplicates = Object.keys(groupedOptionsById).some(
                (key) => key && key !== "undefined" && groupedOptionsById[key]?.length > 1,
            );
            const selectComponent = hasDuplicates ? (
                <BTSelectDuplicate
                    id={filter.key}
                    data-testid={filter.key}
                    disabled={isDisabled}
                    onChange={handleChangeCustom}
                    onBlur={handleBlur}
                    treeData={options[entityFilter.options]}
                    value={multiselectValue as number[]}
                    noTreeDataPlaceholder={entityFilter.placeholderText}
                    filterMode
                    dropdownCanExpand={selectDropdownsCanExpand}
                />
            ) : (
                <BTSelect
                    id={filter.key}
                    data-testid={filter.key}
                    disabled={isDisabled}
                    onChange={handleChangeCustom}
                    onBlur={handleBlur}
                    treeData={options[entityFilter.options]}
                    value={
                        !Array.isArray(multiselectValue)
                            ? multiselectValue
                            : multiselectValue.filter((val) => groupedOptionsById[val]?.length > 0)
                    }
                    noTreeDataPlaceholder={entityFilter.placeholderText}
                    multiple
                    filterMode
                    dropdownCanExpand={selectDropdownsCanExpand}
                />
            );
            return (
                <BTCol
                    className="FilterPresentational-FilterElement"
                    span={isInPopup || useDrawer ? 24 : 3}
                >
                    <BTFormItem label={label}>{selectComponent}</BTFormItem>
                </BTCol>
            );
        }
        case FilterType.SearchField: {
            return (
                <BTCol
                    className="FilterPresentational-FilterElement"
                    span={isInPopup || useDrawer ? 24 : 3}
                >
                    <BTFormItem label={label}>
                        <BTInput<undefined>
                            id={filter.key}
                            data-testid={filter.key}
                            value={selectedValue as string}
                            disabled={isDisabled}
                            onChange={handleChangeCustom}
                        />
                    </BTFormItem>
                </BTCol>
            );
        }
        case FilterType.TextField: {
            return (
                <BTCol
                    className="FilterPresentational-FilterElement"
                    span={isInPopup || useDrawer ? 24 : 3}
                >
                    <BTFormItem label={label}>
                        <BTInput<undefined>
                            data-testid={filter.key}
                            id={filter.key}
                            value={selectedValue as string}
                            disabled={isDisabled}
                            onChange={handleChangeCustom}
                            onBlur={() => {}}
                        />
                    </BTFormItem>
                </BTCol>
            );
        }
        case FilterType.NumberField: {
            const numberValue = new NumberRangeValue(selectedValue);
            const numberRangeEntity: INumberRangeEntity = {
                value: numberValue.NumberValue ? numberValue.NumberValue : null,
                options: options[entityFilter.options],
            };
            const formValue: INumberRangeFormValues = {
                numberValue: numberValue.NumberValue,
                selectedItem: numberValue.SelectedValue,
            };

            return (
                <BTCol className="FilterPresentational-FilterElement">
                    <BTFormItem label={label}>
                        <NumberRange<IFilterFormValues>
                            id="items"
                            onChange={(key, value) => handleNumberRange(filter.key, value)}
                            onBlur={handleBlur}
                            value={formValue}
                            entity={numberRangeEntity}
                            title=""
                            useCurrency={entityFilter.useCurrency}
                        />
                    </BTFormItem>
                </BTCol>
            );
        }
        case FilterType.DateGroups: {
            const dateValue = new DateRangeValue(selectedValue);
            const dateRangeEntity: IDateRangeEntity = {
                dateRange: [
                    dateValue.StartDate ? moment(dateValue.StartDate) : undefined,
                    dateValue.EndDate ? moment(dateValue.EndDate) : undefined,
                ] as RangeValue<moment.Moment>,
                options: options[entityFilter.options],
            };
            const formValue: IDateRangeFormValues = {
                dateRange: [
                    dateValue.StartDate ? moment(dateValue.StartDate) : undefined,
                    dateValue.EndDate ? moment(dateValue.EndDate) : undefined,
                ] as RangeValue<moment.Moment>,
                selectedItem: dateValue.SelectedValue,
            };

            return (
                <BTCol className="FilterPresentational-FilterElement">
                    <BTFormItem label={label}>
                        <DateRange
                            id={entityFilter.key}
                            title=""
                            onBlur={handleBlur}
                            isDisabled={isDisabled}
                            onChange={handleDateChange}
                            onDateRangeChange={onDateRangeChange}
                            isWeekDateDisabled={props.isWeekDateDisabled}
                            isSemiMonthDateDisabled={props.isSemiMonthDateDisabled}
                            entity={dateRangeEntity}
                            value={formValue}
                        />
                    </BTFormItem>
                </BTCol>
            );
        }
        case FilterType.NumberFilter: {
            return (
                <BTCol className="FilterPresentational-FilterElement">
                    <BTFormItem label={label}>
                        <BTInputNumber
                            id={filter.key}
                            data-testid={filter.key}
                            min={0}
                            max={999}
                            className="numberRange--inputNumber"
                            value={selectedValue as number}
                            precision={0}
                            disabled={isDisabled}
                            onChange={(field, value) =>
                                handleChangeCustom(field, value as SelectedValueTypes)
                            }
                        />
                    </BTFormItem>
                </BTCol>
            );
        }
        case FilterType.KeywordSearch: {
            const x = selectedValue as KeywordValue;
            return (
                <BTCol className="FilterPresentational-FilterElement" span={useDrawer ? 24 : 5}>
                    <BTFormItem label={label}>
                        <BTSelect
                            id={filter.key}
                            data-testid={filter.key}
                            disabled={isDisabled}
                            onChange={(field, value) =>
                                handleKeywordSearch(filter.key, value, x.KeywordSearchText)
                            }
                            onBlur={setFieldTouched}
                            value={x.SelectedKeywordTypeIndex}
                            treeData={options[entityFilter.options]}
                            noTreeDataPlaceholder={entityFilter.placeholderText}
                            style={{ width: "calc(40%)" }}
                            addonBefore={
                                <BTInput
                                    id="keywordSearch"
                                    data-testid="keywordSearch"
                                    style={{ width: "calc(60%)" }}
                                    value={x.KeywordSearchText}
                                    onChange={(field, value) =>
                                        handleKeywordSearch(
                                            filter.key,
                                            x.SelectedKeywordTypeIndex,
                                            value,
                                        )
                                    }
                                    onBlur={() => {}}
                                />
                            }
                        />
                    </BTFormItem>
                </BTCol>
            );
        }
        case FilterType.Currency: {
            return (
                <BTCol
                    className="FilterPresentational-FilterElement"
                    span={isInPopup || useDrawer ? 24 : 3}
                >
                    <BTFormItem label={label}>
                        <Currency
                            id={filter.key}
                            data-testid={filter.key}
                            value={+selectedValue!}
                            disabled={isDisabled}
                            onChange={handleChangeCustom}
                            onBlur={setFieldTouched}
                        />
                    </BTFormItem>
                </BTCol>
            );
        }
        case FilterType.Time: {
            const format = "HH:mm:ss a";
            const time = selectedValue ? moment(selectedValue as string, format) : undefined;
            return (
                <BTCol className="FilterPresentational-FilterElement">
                    <BTFormItem label={label}>
                        <BTTimePicker<IFilterFormValues>
                            id="items"
                            data-testid={filter.key}
                            disabled={isDisabled}
                            onChange={(key, value) =>
                                handleChangeCustom(filter.key, value?.format(format))
                            }
                            value={time}
                        />
                    </BTFormItem>
                </BTCol>
            );
        }
        case FilterType.BuilderSelect: {
            const builderValue = selectedValue as SelectedLazyFormValue | undefined;
            return (
                <BTCol className="FilterPresentational-FilterElement" span={useDrawer ? 24 : 4}>
                    <BTFormItem label={label}>
                        <BuilderSearch
                            value={builderValue}
                            id={filter.key}
                            data-testid={filter.key}
                            onChange={(field, _, item) => handleChangeBuilderSearch(field, item)}
                            onBlur={() => {}}
                            accountStatus={ClosedAccountTypes.OpenOnly}
                            accountType={-1 as BuilderFilterTypes}
                        />
                    </BTFormItem>
                </BTCol>
            );
        }
        default: {
            return <Text>Error: Invalid Component Type</Text>;
        }
    }
};

const filterOptionsCustomItemRender = (item: BTSelectItem<SavedFilterItem>) => (
    <FilterSelectCustomItem item={item} />
);

interface IFilterSelectProps {
    savedFilters: SavedFilterItem[];
    selectedFilterId: number;
    onBlur: (field: string) => void;
    onChange: (field: string, value: number) => void;
}

const FilterSelect = ({ savedFilters, selectedFilterId, onBlur, onChange }: IFilterSelectProps) => {
    const getGroupedFilterData = (
        standardFilterItems: BTSelectItem<SavedFilterItem>[],
        suggestedFilterItems: BTSelectItem<SavedFilterItem>[],
        savedFilterItems: BTSelectItem<SavedFilterItem>[],
    ) => {
        const filterData = standardFilterItems;

        if (suggestedFilterItems.length > 0) {
            const suggestedFilterGroup = new BTSelectItem<SavedFilterItem>({
                name: "Suggested Filters",
                children: suggestedFilterItems,
            });
            filterData.push(suggestedFilterGroup);
        }

        if (savedFilterItems.length > 0) {
            const savedFilterGroup = new BTSelectItem<SavedFilterItem>({
                name: "Saved Filters",
                children: savedFilterItems,
            });
            filterData.push(savedFilterGroup);
        }

        return filterData;
    };

    const savedFilterData = useMemo(() => {
        const filterItems = savedFilters.map(
            (item) =>
                new BTSelectItem<SavedFilterItem>({
                    id: item.id,
                    name: item.name,
                    extraData: item,
                }),
        );

        const groupedFilters: BTSelectItem<SavedFilterItem>[][] = filterItems.reduce(
            (acc: BTSelectItem<SavedFilterItem>[][], item) => {
                const itemId = parseInt(item.id);
                if (itemId === StandardFilterId) {
                    acc[0].push(item);
                } else if (itemId < StandardFilterId) {
                    acc[1].push(item);
                } else {
                    acc[2].push(item);
                }
                return acc;
            },
            [[], [], []],
        );

        const [standardFilterItems, suggestedFilterItems, savedFilterItems] = groupedFilters;
        // Only group filters if there are suggested filters
        if (suggestedFilterItems.length === 0) {
            return filterItems;
        }

        return getGroupedFilterData(standardFilterItems, suggestedFilterItems, savedFilterItems);
    }, [savedFilters]);

    return (
        <BTSelect
            id="savedFilterDropdown"
            data-testid="savedFilterDropdown"
            className="FilterPresentational-SavedFiltersDropdown"
            onChange={onChange}
            onBlur={onBlur}
            value={selectedFilterId}
            treeData={savedFilterData}
            customItemRender={filterOptionsCustomItemRender}
        />
    );
};

const FilterInternal = (props: InjectedFormikProps<IFilterProps, IFilterFormValues>) => {
    const {
        actionBeingPerformed,
        appliedFilter,
        defaultOpen,
        drawerOpen,
        entity,
        filterTypeEntity,
        isInPopup,
        isSubmitting,
        jobIDs,
        saveFilterHandler,
        showHasUnsavedChanges,
        showSaveButton,
        showSavedFilters,
        useDrawer,
        values,
        handleSubmit,
        onSavedFilterAdded,
        onSavedFilterDeleted,
        onSavedFilterUpdated,
        onSavedFilterUpdatedClick,
        onSavedFilterVisibleChange,
        onSetDefaultFilter,
        onSubmit,
        setDrawerOpen,
        setFieldTouched,
        setFieldValue,
        setFilterCount,
        setFilterPreferences,
        setSelectedFilterId,
        forceWebviewWidth,
    } = props;

    const { isWeekDateDisabled, isSemiMonthDateDisabled, isMonthDateDisabled } =
        useBuilderDateFilterUtils();

    const [showSavedFilterModal, setShowSavedFilterModal] = useState(false);
    const { settingsState: filterSettingsState, setSettingsState: setFilterSettingsState } =
        useSavedViewsSettingsState(MANAGE_SAVED_FILTERS);
    const [hasUnappliedChanges, setHasUnappliedChanges] = useState(false);

    useEffect(() => {
        if (!isInPopup) {
            setSelectedFilterId(values.savedFilter?.id);
        }

        // Converted from a class componentDidMount, just run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSavedFilterVisibleChange = (visible: boolean) => {
        setShowSavedFilterModal(visible);
        onSavedFilterVisibleChange?.(visible);
    };

    const handleAppliedFilterClear = async (event: React.MouseEvent<unknown, MouseEvent>) => {
        event.stopPropagation();
        const clearedItems = entity.items.map((e) => new SelectedFilterItem(e));
        setFieldValue("items", clearedItems);
        void message.success("All filters cleared");
        await onSubmit({ ...values, items: clearedItems });
        setHasUnappliedChanges(false);
        props.onClear?.();
    };

    const handleApplyFilter = useCallback(
        (event?: React.FormEvent) => {
            // Called from both button onClick (receives a MouseEvent) and BTForm's
            // onFinish (receives form values, no event), so guard preventDefault.
            if (typeof event?.preventDefault === "function") {
                event.preventDefault();
            }
            handleSubmit();
            setHasUnappliedChanges(false);
        },
        [handleSubmit],
    );

    const handleCollapseToggle = (key: string | string[]) => {
        // Key will be an empty string/array when closing the panel since it is the only one
        setFilterPreferences(key.length > 0);
    };

    const handleChangeCustom = useCallback(
        (key: string, value: SelectedValueTypes) => {
            const newItems = values.items.map((item) => new SelectedFilterItem(item));
            const indexOfReplaceItem = newItems.findIndex((x) => x.key === key);

            if (indexOfReplaceItem > -1) {
                const oldFilter = newItems[indexOfReplaceItem];
                newItems[indexOfReplaceItem] = { ...oldFilter, selectedValue: value };
            }
            setHasUnappliedChanges(true);
            setFieldValue("items", newItems);
        },
        [values.items, setFieldValue],
    );

    const handleChangeBuilderSearch = useCallback(
        (key: string, selectedItem: BTSelectItem<unknown> | undefined) => {
            const newItems = values.items.map((item) => new SelectedFilterItem(item));
            const indexOfReplaceItem = newItems.findIndex((x) => x.key === key);

            if (indexOfReplaceItem > -1) {
                const oldFilter = newItems[indexOfReplaceItem];
                newItems[indexOfReplaceItem] = {
                    ...oldFilter,
                    selectedValue: selectedItem,
                };
            }
            setHasUnappliedChanges(true);
            setFieldValue("items", newItems);
        },
        [values.items, setFieldValue],
    );

    const handleNumberRange = useCallback(
        (key: string, formValue: INumberRangeFormValues) => {
            const newItems = values.items.map((item) => new SelectedFilterItem(item));
            const indexOfReplaceItem = newItems.findIndex((x) => x.key === key);

            if (indexOfReplaceItem > -1) {
                const newNumberRange = {
                    SelectedValue: formValue.selectedItem,
                    NumberValue:
                        formValue.selectedItem !== NumberRangeOptionItem.All
                            ? formValue.numberValue
                            : 0,
                };
                const oldFilter = newItems[indexOfReplaceItem];
                newItems[indexOfReplaceItem] = { ...oldFilter, selectedValue: newNumberRange };
            }
            setHasUnappliedChanges(true);
            setFieldValue("items", newItems);
        },
        [values.items, setFieldValue],
    );

    const handleKeywordSearch = useCallback(
        (key: string, selectValue: number, textValue: string) => {
            const newItems = values.items.map((item) => new SelectedFilterItem(item));
            const indexOfReplaceItem = newItems.findIndex((x) => x.key === key);

            if (indexOfReplaceItem > -1) {
                const oldFilter = newItems[indexOfReplaceItem];
                newItems[indexOfReplaceItem] = new SelectedFilterItem({
                    key: oldFilter.key,
                    type: oldFilter.type,
                    selectedValue: {
                        SelectedKeywordTypeIndex: selectValue,
                        KeywordSearchText: textValue,
                    },
                });
            }
            setHasUnappliedChanges(true);
            setFieldValue("items", newItems);
        },
        [values.items, setFieldValue],
    );

    const handleDateChange = useCallback(
        (key: string, formValue: IDateRangeFormValues, deferSetFieldValue: boolean = false) => {
            const newItems = values.items.map((item) => new SelectedFilterItem(item));
            const indexOfReplaceItem = newItems.findIndex((x) => x.key === key);

            if (indexOfReplaceItem > -1) {
                const startDate = !isNullOrUndefined(formValue.dateRange?.[0])
                    ? formValue.dateRange[0].format("YYYY-MM-DD")
                    : null;
                const endDate = !isNullOrUndefined(formValue.dateRange?.[1])
                    ? formValue.dateRange[1].format("YYYY-MM-DD")
                    : null;
                const newDateRange = {
                    SelectedValue: formValue.selectedItem,
                    StartDate: startDate,
                    EndDate: endDate,
                };
                const oldFilter = newItems[indexOfReplaceItem];
                newItems[indexOfReplaceItem] = { ...oldFilter, selectedValue: newDateRange };
            }

            if (!deferSetFieldValue) {
                setFieldValue("items", newItems);
                setHasUnappliedChanges(true);
            }
            return newItems[indexOfReplaceItem];
        },
        [values.items, setFieldValue],
    );

    const handleDateRangeChange = useCallback(
        (
            field: string,
            value: DateRangeFilterOptionItem,
            selectedItem?: BTSelectItem<undefined>,
            deferSetFieldValue: boolean = false,
        ) => {
            let selectedDate;
            switch (value) {
                case DateRangeFilterOptionItem["Weekly"]:
                case DateRangeFilterOptionItem["BiWeekly"]:
                    selectedDate = moment();
                    while (isWeekDateDisabled(selectedDate)) {
                        selectedDate.subtract(1, "day");
                    }
                    break;
                case DateRangeFilterOptionItem["SemiMonthly"]:
                    selectedDate = moment();
                    while (isSemiMonthDateDisabled(selectedDate)) {
                        selectedDate.subtract(1, "day");
                    }
                    break;
                case DateRangeFilterOptionItem["Monthly"]:
                    selectedDate = moment();
                    while (isMonthDateDisabled(selectedDate)) {
                        selectedDate.subtract(1, "day");
                    }
                    break;
                case DateRangeFilterOptionItem["Yearly"]:
                    selectedDate = moment();
                    break;
                case DateRangeFilterOptionItem["CustomDates"]:
                    // If we switch the saved filter (deferSetFieldValue = true) we want to use the default Custom Date value. Otherwise we want to keep it blank
                    if (deferSetFieldValue) {
                        return undefined;
                    }
                    selectedDate = undefined;
                    break;
                default:
                    selectedDate = undefined;
                    break;
            }

            const newValue: IDateRangeFormValues = {
                ...values.items,
                dateRange: selectedDate ? [selectedDate, null] : null,
                selectedItem: value,
            };
            return handleDateChange(field, newValue, deferSetFieldValue);
        },
        [
            values.items,
            handleDateChange,
            isWeekDateDisabled,
            isSemiMonthDateDisabled,
            isMonthDateDisabled,
        ],
    );

    const setDefaultGroupDates = (items: SelectedFilterItem[]) => {
        return items.map((item) => {
            // We need to set the default value for the Weekly, Bi-weekly, etc Date Groups
            if (item.type === FilterType.DateGroups) {
                return (
                    handleDateRangeChange(
                        item.key,
                        (item.selectedValue as DateRangeValue).SelectedValue,
                        undefined,
                        true,
                    ) ?? item
                );
            }
            return item;
        });
    };

    const handleSavedFilterChange = async (field: string, value: number) => {
        if (!isInPopup) {
            setSelectedFilterId(value);
        }

        const selectedSavedFilter = entity.savedFilters.find((item) => item.id === value);
        const items = getSelectedFilterItemsFromSavedFilter(entity, selectedSavedFilter);
        setFieldValue("savedFilter", selectedSavedFilter);

        const newItems = setDefaultGroupDates(items);
        setFieldValue("items", newItems);

        await onSubmit({
            items: newItems,
            savedFilter: selectedSavedFilter!,
        });
    };

    const handleUpdateCurrentSavedFilter = async (values: IFilterFormValues) => {
        const newValues: ISavedFilterFormValues = {
            filterName: values.savedFilter.name,
            isDefault: values.savedFilter.isDefault,
            isPrivate: values.savedFilter.isPrivate,
            selectedFilter: values.savedFilter.id,
            value: getFilterString(values),
            filterType: filterTypeEntity,
        };

        await onSavedFilterUpdatedClick(values.savedFilter.id, newValues);
        setHasUnappliedChanges(false);
        // remapping savedFilter to be updated version
        const updatedValues = mapFilterEntityToFormValues(entity, values.savedFilter.id);
        setFieldValue("savedFilter", updatedValues.savedFilter);
        await onSubmit({
            items: values.items,
            savedFilter: updatedValues.savedFilter,
        });
    };

    const handleAddNewSavedFilterClick = () => {
        setFilterSettingsState("addingNew");
    };

    const getExtraActionsDropdown = () => {
        const selectedFilterId = entity.savedFilters.find((x) => x.id === values.savedFilter.id)
            ? values.savedFilter.id
            : StandardFilterId;
        return (
            <BTMenu testid="filterExtraActionsMenu">
                <BTMenu.Item
                    testid="updateFilter"
                    onClick={() => handleUpdateCurrentSavedFilter(values)}
                    disabled={!hasUnappliedChanges || isStandardFilter(selectedFilterId)}
                >
                    Update Saved Filter
                </BTMenu.Item>
                <BTMenu.Item testid="saveAsNewFilter" onClick={handleAddNewSavedFilterClick}>
                    Add New Saved Filter
                </BTMenu.Item>
            </BTMenu>
        );
    };

    const renderFilterHeader = (appliedFilterCount: number) => {
        return (
            <BTRow className="FilterResults" align="middle" justify="space-between">
                <BTCol span={isInPopup ? 24 : undefined}>
                    <BTRow>
                        <BTCol>
                            <Text>FILTER YOUR RESULTS</Text>

                            {appliedFilterCount > 0 && (
                                <Text className="FilterPresentational-AppliedFilterCount">
                                    <Text className="Divider">|</Text>
                                    {appliedFilterCount}{" "}
                                    <BTFormattedPlural
                                        value={appliedFilterCount}
                                        one="FILTER"
                                        other="FILTERS"
                                    />{" "}
                                    APPLIED
                                    <BTButtonDeprecated
                                        type="link"
                                        data-testid="clear"
                                        onClick={handleAppliedFilterClear}
                                    >
                                        clear
                                    </BTButtonDeprecated>
                                </Text>
                            )}
                        </BTCol>
                    </BTRow>
                </BTCol>
                {showSavedFilters && (
                    <BTCol span={isInPopup ? 24 : undefined}>
                        <BTRow
                            align="middle"
                            justify={isInPopup ? "start" : "end"}
                            gutter={10}
                            className={isInPopup ? "FilterPresentational-SavedFilter" : ""}
                        >
                            {showHasUnsavedChanges && (
                                <Text className="FilterPresentational-UnsavedChanges">
                                    Current filter has unsaved changes.
                                </Text>
                            )}
                            {renderSavedFilterDropdown()}
                            {!isInPopup && (
                                <BTCol onClick={(event) => event.stopPropagation()}>
                                    <BTPopover content="Filter Settings" breakWord={false}>
                                        <BTButtonDeprecated
                                            type="link"
                                            data-testid="gridSettings"
                                            onClick={() => {
                                                setFilterSettingsState("open");
                                            }}
                                            icon={<BTIconSettingFilled />}
                                        />
                                    </BTPopover>
                                    <BTDropdown
                                        testid="filter-extra-actions-dropdown"
                                        overlay={getExtraActionsDropdown()}
                                    >
                                        <BTPopover content="More Actions" breakWord={false}>
                                            <BTButtonDeprecated
                                                type="link"
                                                data-testid=""
                                                className="padding-left-xs"
                                                onClick={() => {}}
                                                icon={<BTIconEllipsisOutlined />}
                                            />
                                        </BTPopover>
                                    </BTDropdown>
                                </BTCol>
                            )}
                        </BTRow>
                    </BTCol>
                )}
            </BTRow>
        );
    };

    const renderSavedFilterDropdown = () => {
        if (!showSavedFilters) {
            return null;
        }

        const selectedValueForFilterDropdown = entity.savedFilters.find(
            (x) => x.id === values.savedFilter.id,
        )
            ? values.savedFilter.id
            : StandardFilterId;

        let filterSelect = (
            <BTSelect
                id="savedFilterDropdown"
                data-testid="savedFilterDropdown"
                className="FilterPresentational-SavedFiltersDropdown"
                onChange={handleSavedFilterChange}
                onBlur={setFieldTouched}
                value={selectedValueForFilterDropdown}
                treeData={entity.savedFilters.map(
                    (item) =>
                        new BTSelectItem<SavedFilterItem>({
                            id: item.id,
                            name: item.name,
                            extraData: item,
                        }),
                )}
                customItemRender={filterOptionsCustomItemRender}
            />
        );

        if (isInPopup) {
            filterSelect = <BTFormItem label="Saved Filters">{filterSelect}</BTFormItem>;
        }

        return (
            <>
                {!isInPopup && (
                    <BTCol>
                        <Text>
                            <strong>Saved Filters</strong>
                        </Text>
                    </BTCol>
                )}

                <BTCol
                    onClick={(event) => event.stopPropagation()}
                    span={isInPopup ? 24 : undefined}
                >
                    {filterSelect}
                </BTCol>
            </>
        );
    };

    const renderFilterContent = () => {
        const selectedFilterId = entity.savedFilters.find((x) => x.id === values.savedFilter.id)
            ? values.savedFilter.id
            : StandardFilterId;

        return (
            <>
                <BTRow gutter={10} align="top">
                    {mapFilterItems()}
                </BTRow>
                <div className="flex flex-wrap">
                    <BTButtonDeprecated<FilterFormActions>
                        className="margin-right-xs margin-top-xs"
                        actionBeingPerformed={actionBeingPerformed}
                        htmlType="submit"
                        data-testid="updateResults"
                        loadingAction="update"
                        onClick={handleApplyFilter}
                        type="primary"
                        disabled={!hasUnappliedChanges}
                    >
                        Update Results
                    </BTButtonDeprecated>
                    <BTButtonDeprecated<FilterFormActions>
                        className="margin-right-xs margin-top-xs"
                        actionBeingPerformed={actionBeingPerformed}
                        data-testid="resetFilters"
                        loadingAction="reset"
                        onClick={handleReset}
                        type="secondary"
                    >
                        Reset Filters
                    </BTButtonDeprecated>
                    {showSaveButton && isInPopup && (
                        <>
                            <BTButtonDeprecated
                                className="margin-right-xs margin-top-xs"
                                disabled={actionBeingPerformed !== undefined}
                                type="secondary"
                                data-testid="save"
                                onClick={() => handleSavedFilterVisibleChange(true)}
                            >
                                Save Filter
                            </BTButtonDeprecated>
                            {!isStandardFilter(selectedFilterId) && (
                                <BTButtonDeprecated
                                    className="margin-right-xs margin-top-xs"
                                    disabled={actionBeingPerformed !== undefined}
                                    type="secondary"
                                    data-testid="delete-filter"
                                    onClick={() =>
                                        btDeleteConfirm({
                                            entityName: "Saved Filter",
                                            onOk: async () =>
                                                handleSavedFilterDeleted(selectedFilterId),
                                        })
                                    }
                                >
                                    Delete Filter
                                </BTButtonDeprecated>
                            )}
                        </>
                    )}
                </div>
            </>
        );
    };

    const mapFilterItems = (): JSX.Element[] => {
        return entity.items.map((entityFilter) => {
            const selectedFilterItem = values.items.find((i) => i.key === entityFilter.key);
            return (
                <FilterItemHelper
                    {...props}
                    key={entityFilter.key}
                    entityFilter={entityFilter}
                    options={entity.options}
                    filter={selectedFilterItem}
                    onDateRangeChange={handleDateRangeChange}
                    isWeekDateDisabled={isWeekDateDisabled}
                    isSemiMonthDateDisabled={isSemiMonthDateDisabled}
                    handleChangeCustom={handleChangeCustom}
                    handleDateChange={handleDateChange}
                    handleKeywordSearch={handleKeywordSearch}
                    handleNumberRange={handleNumberRange}
                    handleChangeBuilderSearch={handleChangeBuilderSearch}
                />
            );
        });
    };

    const renderFilterArea = (appliedFilterCount: number) => {
        const filterClass = cn("FilterCollapse", {
            FilterApplied: appliedFilterCount > 0,
        });

        if (isInPopup) {
            return (
                <>
                    {renderFilterHeader(appliedFilterCount)}
                    <Divider className="FilterPresentational-Divider" />
                    {renderFilterContent()}
                </>
            );
        } else {
            return (
                <BTCollapse
                    data-testid="filterCollapsible"
                    bordered={false}
                    onChange={handleCollapseToggle}
                    defaultActiveKey={defaultOpen ? 1 : undefined}
                    expandIcon={({ isActive }) => (
                        <BTIconCaretRightOutlined rotateRight={isActive} />
                    )}
                >
                    <Panel
                        className={filterClass}
                        header={renderFilterHeader(appliedFilterCount)}
                        key="1"
                        data-testid="FilterPresentational-Collapsible"
                    >
                        {renderFilterContent()}
                    </Panel>
                </BTCollapse>
            );
        }
    };

    const renderFilterDrawer = (
        appliedFilterCount: number,
        formWrapper: (children?: React.ReactNode) => React.ReactNode,
    ) => {
        const selectedFilterId =
            !showSavedFilters || !entity.savedFilters.some((x) => x.id === values.savedFilter.id)
                ? StandardFilterId
                : values.savedFilter.id;

        const setDrawerClosed = () => setDrawerOpen?.(false);

        return (
            <>
                <BTDrawer
                    className="FilterDrawer"
                    data-testid="filterDrawer"
                    placement="right"
                    forceRender
                    width={forceWebviewWidth && isWebview() ? "95vw" : 292}
                    closable={false}
                    keyboard={false}
                    onClose={setDrawerClosed}
                    visible={drawerOpen}
                    title={
                        <>
                            <FilterCount
                                count={appliedFilterCount}
                                setFilterCount={setFilterCount}
                            />
                            <div className="flex justify-content-between">
                                <BTTitle
                                    bold
                                    level={3}
                                    className="margin-top-zero margin-bottom-md"
                                >
                                    Filter
                                </BTTitle>
                                <BTButtonDeprecated
                                    data-testid="closeFilter"
                                    onClick={setDrawerClosed}
                                    type="tertiary"
                                    hotkey="close"
                                    icon={<BTIconCloseOutlined />}
                                />
                            </div>
                            {showSavedFilters && (
                                <>
                                    <div className="FilterDrawerSavedDropdown margin-bottom-md">
                                        <FilterSelect
                                            savedFilters={entity.savedFilters}
                                            selectedFilterId={selectedFilterId}
                                            onChange={handleSavedFilterChange}
                                            onBlur={setFieldTouched}
                                        />
                                        <BTPopover
                                            content="Manage Saved Filters"
                                            placement="topLeft"
                                            breakWord={false}
                                        >
                                            <BTButtonDeprecated
                                                type="link"
                                                noShadow
                                                aria-label="Open Filter Settings"
                                                data-testid="openFilterSettings"
                                                className="FilterDrawerSettings margin-left-xs"
                                                onClick={() => {
                                                    setFilterSettingsState("open");
                                                }}
                                                icon={<BTIconEllipsisOutlined />}
                                            />
                                        </BTPopover>
                                    </div>
                                    {showHasUnsavedChanges && (
                                        <>
                                            <div className="margin-bottom-md">
                                                <Text className="FilterPresentational-UnsavedChanges margin-bottom-md">
                                                    Your filter has unsaved changes.
                                                </Text>
                                            </div>
                                            <div className="margin-bottom-md">
                                                {!isStandardFilter(selectedFilterId) && (
                                                    <BTButtonDeprecated
                                                        type="primary"
                                                        data-testid="saveFilterChanges"
                                                        className="margin-right-xs"
                                                        onClick={() =>
                                                            handleUpdateCurrentSavedFilter(values)
                                                        }
                                                        disabled={isSubmitting}
                                                    >
                                                        Save
                                                    </BTButtonDeprecated>
                                                )}
                                                <BTButtonDeprecated
                                                    data-testid="newSavedFilter"
                                                    className="margin-right-xs"
                                                    onClick={handleAddNewSavedFilterClick}
                                                    disabled={isSubmitting}
                                                >
                                                    Save As
                                                </BTButtonDeprecated>
                                                <BTButtonDeprecated
                                                    data-testid="resetFilterChanges"
                                                    onClick={handleReset}
                                                    disabled={isSubmitting}
                                                >
                                                    Reset
                                                </BTButtonDeprecated>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    }
                    footer={
                        <div className="text-right">
                            <BTButtonDeprecated<FilterFormActions>
                                className="margin-right-xs"
                                data-testid="clearAllFilters"
                                onClick={handleAppliedFilterClear}
                                disabled={isSubmitting}
                            >
                                Clear all
                            </BTButtonDeprecated>
                            <BTButtonDeprecated<FilterFormActions>
                                htmlType="submit"
                                onClick={handleApplyFilter}
                                data-testid="applyFilters"
                                type="primary"
                                disabled={!hasUnappliedChanges || isSubmitting}
                            >
                                Apply filter
                            </BTButtonDeprecated>
                        </div>
                    }
                >
                    {formWrapper(<PageSection>{mapFilterItems()}</PageSection>)}
                </BTDrawer>
            </>
        );
    };

    const updateFiltersBeforeSubmit = (filterProps: IFilterProps) => {
        const values = mapFilterEntityToFormValues(
            filterProps.entity,
            filterProps.selectedFilterId,
            undefined,
        );
        setFieldValue("items", values.items);
        setFieldValue("savedFilter", values.savedFilter);
        return values;
    };

    const handleReset = async () => {
        const defaultFilterId = entity.savedFilters.find((item) => item.isDefault)!.id;
        if (!isInPopup) {
            setSelectedFilterId(defaultFilterId);
        }
        const resetValues = updateFiltersBeforeSubmit({
            ...props,
            selectedFilterId: defaultFilterId,
        });
        await onSubmit(resetValues);
        setHasUnappliedChanges(false);
    };

    const handleSavedFilterDeleted = async (id: number) => {
        const deletedFilter = entity.savedFilters.find((x) => x.id === id);
        const newSavedFilters = entity.savedFilters.filter((x) => x.id !== id);
        const wasDefaultDeleted = deletedFilter?.isDefault;
        if (wasDefaultDeleted) {
            const standardFilter = newSavedFilters.find((x) => x.id === StandardFilterId);
            if (standardFilter) {
                // WARNING: modifying props directly here
                standardFilter.isDefault = true;
            }
        }
        const updatedFilters =
            id === values.savedFilter.id
                ? updateFiltersBeforeSubmit({
                      ...props,
                      entity: {
                          ...entity,
                          savedFilters: newSavedFilters,
                      },
                  })
                : values;
        await onSavedFilterDeleted(id, updatedFilters, wasDefaultDeleted);
    };

    const handleSaveFilter = async (
        id: number,
        savedFilterValue: ISavedViewsAndFiltersCardFormValues,
    ) => {
        await onSavedFilterAdded(id, savedFilterValue, values);
        setFilterSettingsState("open");
    };

    const handleUpdateFilter = async (
        id: number,
        savedFilterValue: ISavedViewsAndFiltersCardFormValues,
        canEdit: boolean = true,
    ) => {
        await onSavedFilterUpdated(id, savedFilterValue, values, canEdit);
        setFilterSettingsState("open");
    };

    const handleCloseSettingsModal = () => {
        handleSavedFilterVisibleChange(false);
        setFilterSettingsState("closed");
    };

    // Once you edit a filter, saving that from the modal will save the updated filter string as well
    const getCurrentFilterData = () => {
        if (hasUnappliedChanges) {
            return entity.savedFilters.map((f) => {
                if (f.id !== values.savedFilter.id) {
                    return f;
                } else {
                    return new SavedFilterItem({ ...f, value: getFilterString(values) });
                }
            });
        } else {
            return entity.savedFilters;
        }
    };

    const selectedSavedFilter = useMemo(
        () => entity.savedFilters.find((x) => x.id === values.savedFilter?.id),
        [entity.savedFilters, values.savedFilter?.id],
    );

    const appliedFilterCount = useMemo(
        () =>
            getAppliedFilterCount(
                appliedFilter || getSelectedFilterItemsFromSavedFilter(entity, selectedSavedFilter),
                entity,
            ),
        [appliedFilter, entity, selectedSavedFilter],
    );

    // BTDrawer cannot be wrapped in a Form, so we must wrap its content in the form instead.
    const formWrapper = (content?: React.ReactNode) => (
        <BTForm
            onSubmit={handleApplyFilter}
            className="FilterContainer"
            data-testid="FilterContainer"
        >
            {!isInPopup ? (
                <BTModal
                    data-testid="btModalFiltersContainer"
                    title="Manage Saved Filters"
                    setPageTitle={false}
                    width="840px"
                    visible={filterSettingsState !== "closed"}
                    beforeClose={handleCloseSettingsModal}
                    useModalLayout
                    removeBodyPadding
                >
                    <SavedViewsAndFiltersSettings
                        dataType="filter"
                        filterData={getCurrentFilterData()}
                        filterTypeEntity={filterTypeEntity}
                        currentFilterValue={getFilterString(values)}
                        filterHandler={saveFilterHandler}
                        onSaveFilter={handleSaveFilter}
                        onUpdateFilter={handleUpdateFilter}
                        onDeleteFilter={handleSavedFilterDeleted}
                        onSetDefaultFilter={onSetDefaultFilter}
                        gridSettingsState={filterSettingsState}
                        setGridSettingsState={setFilterSettingsState}
                        jobIDs={jobIDs}
                        beforeClose={handleCloseSettingsModal}
                    />
                </BTModal>
            ) : (
                <SavedFilter
                    selectedFilterId={values.savedFilter.id}
                    entity={new SavedFilterEntity({ savedFilters: entity.savedFilters })}
                    filterTypeEntity={filterTypeEntity}
                    currentValue={getFilterString(values)}
                    modalConfig={{
                        beforeClose: () => handleSavedFilterVisibleChange(false),
                        parentRoute: "",
                    }}
                    visible={showSavedFilterModal}
                    onFilterAdded={async (id, savedFilterValue) => {
                        await onSavedFilterAdded(id, savedFilterValue, values);
                        setFieldValue(
                            "savedFilter",
                            new SavedFilterItem({
                                id,
                                value: savedFilterValue.value,
                                name: savedFilterValue.filterName,
                            }),
                        );
                    }}
                    onFilterUpdated={async (id, savedFilterValues, canEdit) =>
                        await onSavedFilterUpdated(id, savedFilterValues, values, canEdit)
                    }
                    jobIDs={jobIDs}
                    handler={saveFilterHandler}
                />
            )}
            {content}
        </BTForm>
    );

    if (useDrawer) {
        return renderFilterDrawer(appliedFilterCount, formWrapper);
    }

    return formWrapper(renderFilterArea(appliedFilterCount));
};

const withFormikConfig: WithFormikConfig<IFilterProps, IFilterFormValues> = {
    mapPropsToValues: (props) => {
        return mapFilterEntityToFormValues(
            props.entity,
            props.selectedFilterId || props.entity.savedFilters.find((f) => f.isDefault)?.id,
            // When the component is remounted, these values may be reinitialized from values stored in context
            props.appliedFilter ?? props.jobPickerFilterOverride,
        );
    },

    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,

    handleSubmit: async (values: IFilterFormValues, { props, setSubmitting }) => {
        setSubmitting(true);
        await props.onSubmit(values);
        setSubmitting(false);
    },
};
export const FilterPresentational = withFormik<IFilterProps, IFilterFormValues>(withFormikConfig)(
    FilterInternal,
);
